import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skandi-Token · dezente Farbcodierung pro Priorität · KEINE ICONS
const PRIO_TONE = {
  hoch:    { dot: "bg-brand-darkred", chip: "bg-brand-darkred/12 text-brand-darkred border-brand-darkred/30", label: "Hoch" },
  mittel:  { dot: "bg-saffron",       chip: "bg-saffron/25 text-clay-dark border-saffron/50",                 label: "Mittel" },
  niedrig: { dot: "bg-sage-dark",     chip: "bg-sage-dark/12 text-sage-dark border-sage-dark/30",             label: "Niedrig" },
};

const RISIKO_TONE = {
  hoch:    { card: "border-brand-darkred/40 bg-peach-50",    text: "text-brand-darkred", label: "Hohes Prüfrisiko" },
  mittel:  { card: "border-saffron/60 bg-cream-50",          text: "text-clay-dark",     label: "Mittleres Prüfrisiko" },
  niedrig: { card: "border-sage-dark/40 bg-cream-50",        text: "text-sage-dark",     label: "Niedriges Prüfrisiko" },
};

const DECISION_LABEL = {
  uebernommen: "Übernommen",
  bearbeitet:  "Bearbeitet & übernommen",
  abgelehnt:   "Abgelehnt",
};

const DECISION_TONE = {
  uebernommen: "bg-sage-dark/15 text-sage-dark border-sage-dark/40",
  bearbeitet:  "bg-sage-dark/15 text-sage-dark border-sage-dark/40",
  abgelehnt:   "bg-sand-200 text-sand-700 border-sand-300",
};

const KATEGORIEN = ["Vollständigkeit", "Plausibilität", "NBA-Konsistenz", "Risiken", "Kassenbrief", "Höherstufung"];

const SOURCE_FIELDS = [
  { key: "sis",          label: "SIS-Text (A1–A6)" },
  { key: "plan",         label: "Maßnahmenplan" },
  { key: "kassenbrief",  label: "Kassenbrief" },
  { key: "hoeherstufung",label: "Höherstufungs-Schreiben" },
];

const DRAFT_KIND_FOR_FIELD = {
  sis: ["sis"],
  plan: ["massnahmen"],
  kassenbrief: ["kassenbrief"],
  hoeherstufung: ["hoehergruppe"],
};

export default function PkiMdSimulator({ initialSis, initialPlan, initialKassenbrief, initialHoeherstufung, onUsageChange }) {
  const [sisText, setSisText] = useState(initialSis || "");
  const [planText, setPlanText] = useState(initialPlan || "");
  const [kassenbriefText, setKassenbriefText] = useState(initialKassenbrief || "");
  const [hoeherstufungText, setHoeherstufungText] = useState(initialHoeherstufung || "");
  const [customerLabel, setCustomerLabel] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // suggestionsMap: { befund_id: { pflegeziel, massnahme, evaluation, vorschlag_text } }
  const [suggestionsMap, setSuggestionsMap] = useState({});
  // decisionsMap: { befund_id: { entscheidung, text_uebernommen, at } }
  const [decisionsMap, setDecisionsMap] = useState({});
  const [buffer, setBuffer] = useState([]);
  const [suggBusy, setSuggBusy] = useState(false);
  const [suggStage, setSuggStage] = useState(""); // user-sichtbarer Status, z. B. "KI verbindet"
  const [suggProgress, setSuggProgress] = useState(0); // 0..100
  const [suggJobId, setSuggJobId] = useState(null);
  const suggAbortRef = useRef({ cancelled: false });
  const [decideBusy, setDecideBusy] = useState({}); // { befund_id: bool }
  const [filterPrio, setFilterPrio] = useState("alle");
  const [filterKat, setFilterKat] = useState("alle");

  const setterFor = (field) => {
    if (field === "sis") return setSisText;
    if (field === "plan") return setPlanText;
    if (field === "kassenbrief") return setKassenbriefText;
    if (field === "hoeherstufung") return setHoeherstufungText;
    return () => {};
  };
  const valueFor = (field) => ({ sis: sisText, plan: planText, kassenbrief: kassenbriefText, hoeherstufung: hoeherstufungText }[field] || "");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/pflege-ki/drafts`, { withCredentials: true, params: { limit: 100 } });
        if (!cancel) setDrafts(r.data?.drafts || []);
      } catch { /* leise */ }
    })();
    return () => { cancel = true; };
  }, []);

  const draftsByKind = useMemo(() => {
    const out = {};
    for (const d of drafts) {
      if (!d?.kind) continue;
      (out[d.kind] = out[d.kind] || []).push(d);
    }
    return out;
  }, [drafts]);

  const loadDraftIntoField = (field, draftId) => {
    if (!draftId) return;
    const d = drafts.find((x) => x.id === draftId);
    if (!d) return;
    setterFor(field)(d.output_text || "");
    if (!customerLabel && d.customer_label) setCustomerLabel(d.customer_label);
    toast.success(`„${d.customer_label || "Entwurf"}" geladen.`);
  };

  const run = async () => {
    if (!sisText.trim() && !planText.trim() && !kassenbriefText.trim() && !hoeherstufungText.trim()) {
      toast.error("Bitte mindestens einen der vier Texte einfügen.");
      return;
    }
    setBusy(true); setResult(null); setSuggestionsMap({}); setDecisionsMap({}); setBuffer([]);
    try {
      const r = await axios.post(`${API}/pflege-ki/md-v2/audit`, {
        sis_text: sisText,
        plan_text: planText,
        kassenbrief_text: kassenbriefText,
        hoeherstufung_text: hoeherstufungText,
        customer_label: customerLabel || null,
      }, { withCredentials: true });
      setResult(r.data);
      onUsageChange?.();
      toast.success("MD-Audit abgeschlossen.");
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Audit fehlgeschlagen.";
      if (status === 429) toast.error(typeof msg === "string" ? msg : "Tageslimit erreicht.");
      else toast.error(typeof msg === "string" ? msg : "MD-Simulator-Fehler.");
    } finally { setBusy(false); }
  };

  const fetchSuggestions = async (befundIds = null) => {
    if (!result?.audit_id) return;
    // Reset abort flag
    suggAbortRef.current = { cancelled: false };
    setSuggBusy(true);
    setSuggStage("in der Warteschlange");
    setSuggProgress(2);
    setSuggJobId(null);

    let jobId = null;
    try {
      // 1) Job erstellen
      const r = await axios.post(`${API}/pflege-ki/md-v2/suggest`,
        { audit_id: result.audit_id, ...(befundIds ? { befund_ids: befundIds } : {}) },
        { withCredentials: true });
      onUsageChange?.();

      // Sonderfall: keine relevanten Befunde
      if (r.data?.status === "done" && (r.data?.suggestions || []).length === 0) {
        toast.info("Keine Befunde mit Priorität hoch/mittel.");
        return;
      }
      jobId = r.data?.job_id;
      if (!jobId) throw new Error("Keine job_id erhalten.");
      setSuggJobId(jobId);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Vorschläge konnten nicht angefordert werden.";
      toast.error(typeof msg === "string" ? msg : "LLM-Fehler.");
      setSuggBusy(false); setSuggStage(""); setSuggProgress(0); setSuggJobId(null);
      return;
    }

    // 2) Polling-Schleife (alle 2 s, max 90 s)
    const startedAt = Date.now();
    const maxMs = 90_000;
    while (!suggAbortRef.current.cancelled) {
      if (Date.now() - startedAt > maxMs) {
        toast.error("KI-Vorschläge dauern ungewöhnlich lange. Bitte später erneut versuchen.");
        break;
      }
      // 2 s warten
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 2000));
      if (suggAbortRef.current.cancelled) break;

      try {
        // eslint-disable-next-line no-await-in-loop
        const j = await axios.get(`${API}/pflege-ki/md-v2/jobs/${jobId}`, { withCredentials: true });
        const job = j.data || {};
        setSuggStage(job.stage || "");
        if (typeof job.progress === "number") setSuggProgress(job.progress);

        if (job.status === "done") {
          const list = job.suggestions || [];
          setSuggestionsMap((prev) => {
            const map = { ...prev };
            list.forEach((s) => { if (s.befund_id) map[s.befund_id] = s; });
            return map;
          });
          toast.success(`${list.length} Vorschlag(s) erstellt.`);
          break;
        }
        if (job.status === "error") {
          toast.error(job.error || "KI-Vorschläge fehlgeschlagen.");
          break;
        }
        // status = queued/running → weiter pollen
      } catch (e) {
        // 404 → Job weg; 5xx vom Ingress nicht mehr möglich, weil schnell.
        const msg = e?.response?.data?.detail || "Status-Abruf fehlgeschlagen.";
        toast.error(typeof msg === "string" ? msg : "Polling-Fehler.");
        break;
      }
    }

    setSuggBusy(false); setSuggStage(""); setSuggProgress(0); setSuggJobId(null);
  };

  const cancelSuggestions = () => {
    suggAbortRef.current = { cancelled: true };
    toast.message('Anfrage beendet. Die KI läuft im Hintergrund weiter; das Ergebnis erscheint, sobald du erneut auf "Vorschläge generieren" klickst.');
  };

  const decide = async (befundId, entscheidung, text) => {
    if (!result?.audit_id) return;
    setDecideBusy((m) => ({ ...m, [befundId]: true }));
    try {
      const r = await axios.post(`${API}/pflege-ki/md-v2/decide`, {
        audit_id: result.audit_id,
        befund_id: befundId,
        entscheidung,
        text_uebernommen: text || "",
      }, { withCredentials: true });
      // Aus dem GET den frischen State lesen
      const full = await axios.get(`${API}/pflege-ki/md-v2/audit/${result.audit_id}`, { withCredentials: true });
      setDecisionsMap(full.data?.decisions_map || {});
      setBuffer(full.data?.uebernahme_buffer || []);
      toast.success(`Entscheidung gespeichert: ${DECISION_LABEL[entscheidung] || entscheidung}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Entscheidung konnte nicht gespeichert werden.";
      toast.error(typeof msg === "string" ? msg : "Fehler.");
    } finally { setDecideBusy((m) => ({ ...m, [befundId]: false })); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("In Zwischenablage kopiert."); }
    catch { toast.error("Kopieren nicht möglich."); }
  };

  const downloadPdf = () => {
    if (!result?.audit_id) return;
    const url = `${API}/pflege-ki/md-v2/audit/${result.audit_id}/pdf`;
    // Cookie-Auth ist gesetzt; window.open lädt direkt
    window.open(url, "_blank", "noopener");
  };

  const visibleBefunde = useMemo(() => {
    if (!result?.befunde) return [];
    return result.befunde.filter((b) => {
      if (filterPrio !== "alle" && b.prioritaet !== filterPrio) return false;
      if (filterKat !== "alle" && b.kategorie !== filterKat) return false;
      return true;
    });
  }, [result, filterPrio, filterKat]);

  return (
    <div className="space-y-6" data-testid="pki-md-simulator">
      {/* Header */}
      <div className="rounded-3xl border border-cream-300 bg-peach-50 p-6 md:p-8">
        <div className="text-[10.5px] uppercase tracking-[0.3em] font-semibold text-clay">
          Pflege-KI · MD-Prüfungs-Simulator
        </div>
        <h2 className="font-display text-2xl md:text-[28px] text-sand-800 mt-1.5 leading-tight">
          Strukturierte Vor-Prüfung deiner Pflegedokumentation
        </h2>
        <p className="text-sand-700 text-[13.5px] md:text-sm mt-2 max-w-3xl leading-relaxed">
          Sechs Prüfkategorien · deterministische Regeln · konkrete Befunde mit Vorschlag.
          Optional liefert die KI strukturierte Textbausteine (Pflegeziel · Maßnahme · Evaluation),
          die du <strong>vor</strong> der Übernahme bearbeiten oder ablehnen kannst.
        </p>
        <p className="text-sand-600 text-[12px] mt-2 max-w-3xl leading-relaxed">
          Hinweis: Übernommene Vorschläge werden <strong>nicht automatisch</strong> in SIS oder
          Maßnahmenplan geschrieben, sondern landen im Übernahme-Buffer zur kontrollierten manuellen Übernahme.
        </p>
      </div>

      {/* Kundenkennzeichen */}
      <div className="rounded-2xl border border-cream-300 bg-white p-4 flex items-center gap-3 flex-wrap">
        <label className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">
          Kunden-Bezug (optional)
        </label>
        <input
          type="text"
          value={customerLabel}
          onChange={(e) => setCustomerLabel(e.target.value)}
          placeholder="z. B. „Frau M. (Kd-#1342)"
          data-testid="pki-md-customer-label"
          className="flex-1 min-w-[200px] rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[13px] text-sand-800 focus:outline-none focus:border-terra"
        />
      </div>

      {/* Eingabe-Felder */}
      <div className="grid md:grid-cols-2 gap-4">
        {SOURCE_FIELDS.map((f) => {
          const kinds = DRAFT_KIND_FOR_FIELD[f.key] || [];
          const options = kinds.flatMap((k) => draftsByKind[k] || []);
          return (
            <div key={f.key} className="rounded-2xl border border-cream-300 bg-white p-5"
                 data-testid={`pki-md-field-${f.key}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <label className="block text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">
                  {f.label}
                </label>
                {options.length > 0 && (
                  <select
                    onChange={(e) => { loadDraftIntoField(f.key, e.target.value); e.target.value = ""; }}
                    defaultValue=""
                    data-testid={`pki-md-draft-picker-${f.key}`}
                    className="text-[12px] rounded-lg border border-cream-300 bg-cream-50 px-2 py-1 text-sand-700 focus:outline-none focus:border-terra"
                  >
                    <option value="" disabled>Aus Entwürfen laden …</option>
                    {options.map((d) => (
                      <option key={d.id} value={d.id}>
                        {(d.customer_label || "Ohne Label") + " · " + new Date(d.updated_at || d.created_at).toLocaleDateString("de-DE")}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <textarea
                rows={8}
                value={valueFor(f.key)}
                onChange={(e) => setterFor(f.key)(e.target.value)}
                placeholder={`${f.label} hier einfügen oder Entwurf laden …`}
                data-testid={`pki-md-text-${f.key}`}
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[13px] font-mono text-sand-800 focus:outline-none focus:border-terra"
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-sand-600 max-w-2xl">
          Deterministische Prüfung läuft ohne KI-Aufruf. Verbesserungs-Vorschläge holst du dir
          anschließend explizit per Button.
        </div>
        <Button onClick={run} disabled={busy}
          data-testid="pki-md-run"
          className="h-11 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white font-semibold px-7">
          {busy ? "Prüfung läuft …" : "MD-Audit starten"}
        </Button>
      </div>

      {result && (
        <div className="space-y-5" data-testid="pki-md-result">
          <DashboardCard result={result} onPdf={downloadPdf} />

          {/* Kategorie-Übersicht */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="pki-md-kategorien">
            {KATEGORIEN.map((k) => {
              const row = (result.kategorien_summary || []).find((x) => x.kategorie === k) || { anzahl: 0, hoch: 0, mittel: 0, niedrig: 0 };
              return (
                <button
                  key={k}
                  onClick={() => setFilterKat((cur) => (cur === k ? "alle" : k))}
                  data-testid={`pki-md-kat-card-${k}`}
                  className={`text-left rounded-2xl border p-4 transition-colors ${
                    filterKat === k ? "border-brand-darkred bg-peach-50" : "border-cream-300 bg-white hover:bg-cream-50"
                  }`}
                >
                  <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">{k}</div>
                  <div className="font-display text-2xl text-sand-800 mt-0.5">{row.anzahl}</div>
                  <div className="flex gap-2 mt-2 text-[11px] text-sand-700">
                    <PrioMini prio="hoch"    count={row.hoch} />
                    <PrioMini prio="mittel"  count={row.mittel} />
                    <PrioMini prio="niedrig" count={row.niedrig} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Filter + Vorschläge-Button */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500">Filter</span>
              {["alle", "hoch", "mittel", "niedrig"].map((p) => (
                <button
                  key={p}
                  onClick={() => setFilterPrio(p)}
                  data-testid={`pki-md-filter-prio-${p}`}
                  className={`text-[12px] px-3 h-7 rounded-full border ${
                    filterPrio === p
                      ? "bg-sand-800 text-cream-50 border-sand-800"
                      : "bg-white text-sand-700 border-cream-300 hover:border-sand-400"
                  }`}
                >
                  {p === "alle" ? "Alle Prioritäten" : PRIO_TONE[p].label}
                </button>
              ))}
              {filterKat !== "alle" && (
                <button
                  onClick={() => setFilterKat("alle")}
                  data-testid="pki-md-filter-kat-reset"
                  className="text-[12px] px-3 h-7 rounded-full border border-cream-300 bg-cream-50 text-sand-700 hover:bg-white"
                >
                  Kategorie: {filterKat} · entfernen
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {suggBusy ? (
                <>
                  <div className="flex flex-col items-end" data-testid="pki-md-suggest-progress">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-clay">
                      {suggStage || "in der Warteschlange"} · {suggProgress}%
                    </div>
                    <div className="h-1.5 w-44 mt-1 rounded-full bg-cream-200 overflow-hidden">
                      <div className="h-full bg-brand-darkred transition-all" style={{ width: `${Math.max(2, suggProgress)}%` }} />
                    </div>
                  </div>
                  <Button
                    onClick={cancelSuggestions}
                    data-testid="pki-md-suggest-cancel"
                    variant="outline"
                    className="h-10 rounded-full border-cream-300 text-sand-700 hover:bg-cream-50 font-semibold px-5"
                  >
                    Anzeige abbrechen
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => fetchSuggestions(null)}
                  disabled={(result.befunde || []).length === 0}
                  data-testid="pki-md-suggest"
                  variant="outline"
                  className="h-10 rounded-full border-brand-rose text-clay-dark hover:bg-peach-50 font-semibold px-5"
                >
                  Vorschläge generieren
                </Button>
              )}
            </div>
          </div>

          {/* Befund-Liste */}
          <div className="space-y-3" data-testid="pki-md-befunde">
            {visibleBefunde.length === 0 ? (
              <div className="rounded-2xl border border-cream-300 bg-cream-50 p-6 text-center text-sand-700">
                Keine Befunde in dieser Auswahl.
              </div>
            ) : (
              visibleBefunde.map((b) => (
                <BefundCard
                  key={b.id}
                  befund={b}
                  suggestion={suggestionsMap[b.id]}
                  decision={decisionsMap[b.id]}
                  busy={decideBusy[b.id]}
                  suggBusy={suggBusy}
                  suggStage={suggStage}
                  suggProgress={suggProgress}
                  onSuggest={() => fetchSuggestions([b.id])}
                  onDecide={(entscheidung, text) => decide(b.id, entscheidung, text)}
                  onCopy={copy}
                />
              ))
            )}
          </div>

          {/* Übernahme-Buffer */}
          {buffer.length > 0 && (
            <BufferBlock buffer={buffer} onCopy={copy} />
          )}

          {/* Disclaimer */}
          <div className="rounded-2xl border border-cream-300 bg-cream-50 p-4 text-[12.5px] text-sand-700">
            {result.disclaimer}
          </div>
        </div>
      )}
    </div>
  );
}

function DashboardCard({ result, onPdf }) {
  const score = result.score || {};
  const tone = RISIKO_TONE[score.label] || RISIKO_TONE.niedrig;
  return (
    <div className={`rounded-3xl border p-6 md:p-7 ${tone.card}`} data-testid="pki-md-score">
      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-[10.5px] uppercase tracking-[0.3em] font-semibold text-sand-600">
              Gesamteinschätzung
            </div>
            <button
              onClick={onPdf}
              data-testid="pki-md-pdf"
              className="text-[12px] underline text-clay-dark hover:text-brand-darkred font-semibold"
            >
              PDF exportieren
            </button>
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <div className="font-display text-5xl md:text-6xl text-sand-800 leading-none" data-testid="pki-md-score-value">
              {score.score ?? 0}
            </div>
            <div className="text-[13px] text-sand-600">/ 100</div>
            <div className={`text-[12px] uppercase tracking-[0.22em] font-semibold ${tone.text}`}>
              · {tone.label}
            </div>
          </div>
          <p className="text-sand-700 text-[13.5px] mt-2 max-w-2xl leading-relaxed">
            {score.zusammenfassung}
          </p>
        </div>
        <div className="flex md:flex-col gap-2 md:items-stretch">
          <ScorePill prio="hoch"    count={score.hoch || 0} />
          <ScorePill prio="mittel"  count={score.mittel || 0} />
          <ScorePill prio="niedrig" count={score.niedrig || 0} />
        </div>
      </div>
    </div>
  );
}

function ScorePill({ prio, count }) {
  const t = PRIO_TONE[prio];
  return (
    <div className={`inline-flex items-center justify-between gap-3 px-4 h-10 rounded-full border ${t.chip}`}
         data-testid={`pki-md-pill-${prio}`}>
      <span className="inline-flex items-center gap-2">
        <span className={`inline-block h-2 w-2 rounded-full ${t.dot}`} aria-hidden="true" />
        <span className="text-[12px] uppercase tracking-[0.18em] font-semibold">{t.label}</span>
      </span>
      <span className="font-mono text-[15px] font-bold">{count}</span>
    </div>
  );
}

function PrioMini({ prio, count }) {
  const t = PRIO_TONE[prio];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${t.dot}`} aria-hidden="true" />
      <span className="font-mono">{count}</span>
    </span>
  );
}

function BefundCard({ befund, suggestion, decision, busy, suggBusy, suggStage, suggProgress, onSuggest, onDecide, onCopy }) {
  const t = PRIO_TONE[befund.prioritaet] || PRIO_TONE.niedrig;
  const hasStructured = suggestion && (suggestion.pflegeziel || suggestion.massnahme || suggestion.evaluation);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");

  const composedText = useMemo(() => {
    if (!suggestion) return "";
    const parts = [];
    if (suggestion.pflegeziel) parts.push(`Pflegeziel: ${suggestion.pflegeziel}`);
    if (suggestion.massnahme)  parts.push(`Maßnahme: ${suggestion.massnahme}`);
    if (suggestion.evaluation) parts.push(`Evaluation: ${suggestion.evaluation}`);
    return parts.join("\n");
  }, [suggestion]);

  const openEdit = () => {
    setEditText(composedText || suggestion?.vorschlag_text || "");
    setEditing(true);
  };

  const submitEdit = () => {
    onDecide("bearbeitet", editText.trim());
    setEditing(false);
  };

  return (
    <div className="rounded-2xl border border-cream-300 bg-white p-5"
         data-testid={`pki-md-befund-${befund.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-2 px-3 h-7 rounded-full border text-[11px] uppercase tracking-[0.18em] font-semibold ${t.chip}`}>
            <span className={`inline-block h-2 w-2 rounded-full ${t.dot}`} aria-hidden="true" />
            {t.label}
          </span>
          <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500">
            {befund.kategorie}
          </span>
          <span className="text-[11px] text-sand-500">·</span>
          <span className="text-[12px] text-sand-700">{befund.fundstelle}</span>
        </div>
        {decision && (
          <span
            data-testid={`pki-md-status-${befund.id}`}
            className={`inline-flex items-center gap-2 px-3 h-7 rounded-full border text-[11px] uppercase tracking-[0.18em] font-semibold ${DECISION_TONE[decision.entscheidung] || ""}`}
          >
            {DECISION_LABEL[decision.entscheidung] || decision.entscheidung}
          </span>
        )}
      </div>

      <div className="mt-3 text-[14px] text-sand-800 font-semibold leading-snug">
        {befund.problem}
      </div>

      <div className="mt-2 text-[12.5px] text-sand-700 leading-relaxed">
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mr-1">Warum wichtig</span>
        {befund.warum_wichtig}
      </div>

      <div className="mt-3 rounded-xl border border-cream-300 bg-cream-50 p-3">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-clay-dark mb-1">Vorschlag</div>
        <div className="text-[13px] text-sand-800 leading-relaxed">{befund.vorschlag}</div>
      </div>

      {/* KI-Vorschlag-Block */}
      {hasStructured && (
        <div className="mt-3 rounded-xl border border-brand-rose/50 bg-peach-50 p-4"
             data-testid={`pki-md-llm-${befund.id}`}>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-darkred mb-2">
            KI-Vorschlag · strukturiert
          </div>
          {editing ? (
            <div className="space-y-2">
              <textarea
                rows={6}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                data-testid={`pki-md-edit-text-${befund.id}`}
                className="w-full rounded-xl border border-cream-300 bg-white px-3 py-2 text-[13px] font-mono text-sand-800 focus:outline-none focus:border-terra"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={submitEdit}
                  disabled={busy || !editText.trim()}
                  data-testid={`pki-md-edit-save-${befund.id}`}
                  className="h-9 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white font-semibold px-4 text-[12.5px]"
                >
                  Bearbeitet übernehmen
                </Button>
                <button
                  onClick={() => setEditing(false)}
                  data-testid={`pki-md-edit-cancel-${befund.id}`}
                  className="text-[12.5px] underline text-clay-dark hover:text-brand-darkred"
                >
                  Bearbeitung abbrechen
                </button>
              </div>
            </div>
          ) : (
            <>
              {suggestion.pflegeziel && (
                <div className="text-[13px] text-sand-800 leading-relaxed mb-1.5">
                  <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-clay mr-1.5">Pflegeziel</span>
                  {suggestion.pflegeziel}
                </div>
              )}
              {suggestion.massnahme && (
                <div className="text-[13px] text-sand-800 leading-relaxed mb-1.5">
                  <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-clay mr-1.5">Maßnahme</span>
                  <span className="whitespace-pre-wrap">{suggestion.massnahme}</span>
                </div>
              )}
              {suggestion.evaluation && (
                <div className="text-[13px] text-sand-800 leading-relaxed">
                  <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-clay mr-1.5">Evaluation</span>
                  {suggestion.evaluation}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => onDecide("uebernommen", composedText)}
                  disabled={busy}
                  data-testid={`pki-md-accept-${befund.id}`}
                  className="h-9 rounded-full bg-sage-dark hover:bg-sage-dark/90 text-cream-50 font-semibold px-4 text-[12.5px]"
                >
                  Übernehmen
                </Button>
                <Button
                  onClick={openEdit}
                  disabled={busy}
                  data-testid={`pki-md-edit-${befund.id}`}
                  variant="outline"
                  className="h-9 rounded-full border-clay text-clay-dark hover:bg-cream-50 font-semibold px-4 text-[12.5px]"
                >
                  Bearbeiten
                </Button>
                <Button
                  onClick={() => onDecide("abgelehnt", "")}
                  disabled={busy}
                  data-testid={`pki-md-reject-${befund.id}`}
                  variant="outline"
                  className="h-9 rounded-full border-cream-300 text-sand-700 hover:bg-cream-50 font-semibold px-4 text-[12.5px]"
                >
                  Ablehnen
                </Button>
                <button
                  onClick={() => onCopy(composedText)}
                  className="text-[11.5px] underline text-sand-600 hover:text-clay-dark ml-auto"
                  data-testid={`pki-md-copy-${befund.id}`}
                >
                  Kopieren
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {!hasStructured && (
        <div className="mt-3">
          <button
            onClick={onSuggest}
            disabled={suggBusy}
            data-testid={`pki-md-suggest-one-${befund.id}`}
            className="text-[12px] underline text-clay-dark hover:text-brand-darkred font-semibold disabled:opacity-50"
          >
            {suggBusy ? `${suggStage || "in der Warteschlange"} · ${suggProgress}%` : "Verbesserungs-Vorschlag für diesen Befund generieren"}
          </button>
        </div>
      )}
    </div>
  );
}

function BufferBlock({ buffer, onCopy }) {
  const fullText = buffer.map((b, i) => {
    return `[${i + 1}] ${b.kategorie} · ${b.fundstelle}\n${b.problem}\n${b.text}\n`;
  }).join("\n");
  return (
    <div className="rounded-3xl border border-sage-dark/40 bg-cream-50 p-5 md:p-6"
         data-testid="pki-md-buffer">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.3em] font-semibold text-sage-dark">
            Übernahme-Buffer · {buffer.length} Eintrag/Einträge
          </div>
          <h3 className="font-display text-xl text-sand-800 mt-1">
            Vom Nutzer freigegebene Vorschläge
          </h3>
          <p className="text-[12px] text-sand-600 mt-1 max-w-2xl leading-relaxed">
            Diese Vorschläge wurden <strong>nicht</strong> automatisch in SIS oder Maßnahmenplan übernommen.
            Du kannst sie kontrolliert per Hand kopieren und in deine Dokumentation einsetzen.
          </p>
        </div>
        <button
          onClick={() => onCopy(fullText)}
          data-testid="pki-md-buffer-copy-all"
          className="text-[12px] underline text-clay-dark hover:text-brand-darkred font-semibold"
        >
          Alle Einträge kopieren
        </button>
      </div>
      <div className="space-y-2">
        {buffer.map((b, i) => {
          const t = PRIO_TONE[b.prioritaet] || PRIO_TONE.niedrig;
          return (
            <div key={b.befund_id} className="rounded-xl border border-cream-300 bg-white p-3"
                 data-testid={`pki-md-buffer-entry-${b.befund_id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">#{i + 1}</span>
                <span className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full border text-[10.5px] uppercase tracking-[0.18em] font-semibold ${t.chip}`}>
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${t.dot}`} aria-hidden="true" />
                  {t.label}
                </span>
                <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500">{b.kategorie}</span>
                <span className="text-[11px] text-sand-500">·</span>
                <span className="text-[12px] text-sand-700">{b.fundstelle}</span>
                {b.modus === "bearbeitet" && (
                  <span className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-clay">· bearbeitet</span>
                )}
              </div>
              <div className="text-[12.5px] text-sand-800 font-semibold mb-1">{b.problem}</div>
              <div className="text-[13px] text-sand-800 leading-relaxed whitespace-pre-wrap">{b.text}</div>
              <button
                onClick={() => onCopy(b.text)}
                className="text-[11px] underline text-sand-600 hover:text-clay-dark mt-2"
              >
                Diesen Eintrag kopieren
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
